import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';

interface PerformanceByStatusChartProps {
  data: Array<{
    status: string;
    count: number;
    average_progress: number;
  }>;
  loading?: boolean;
}

const PerformanceByStatusChart: React.FC<PerformanceByStatusChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance by Status</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance by Status</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No performance data available
        </div>
      </div>
    );
  }

  const STATUS_COLORS = {
    'new': '#8b5cf6',           // Purple
    'draft': '#6b7280',          // Gray
    'in_progress': '#3b82f6',    // Blue
    'on_hold': '#f59e0b',        // Yellow
    'risk_of_delay': '#f97316',  // Orange
    'closed': '#10b981',         // Green
    'fully_implemented': '#059669', // Dark Green
    'rejected': '#ef4444'        // Red
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance by Status</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="status" 
              tick={{ fontSize: 12 }}
              tickFormatter={formatStatus}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
            <Tooltip 
              formatter={(value: number, name: string) => [
                name === 'count' ? `${value} workshops` : `${value.toFixed(1)}%`,
                name === 'count' ? 'Workshop Count' : 'Average Progress'
              ]}
              labelFormatter={(label) => `Status: ${formatStatus(label)}`}
            />
            <Legend />
            <Bar 
              yAxisId="left"
              dataKey="count" 
              name="Workshop Count"
              radius={[4, 4, 0, 0]}
            >
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-count-${index}`} 
                  fill={STATUS_COLORS[entry.status as keyof typeof STATUS_COLORS] || '#6b7280'} 
                />
              ))}
            </Bar>
            <Bar 
              yAxisId="right"
              dataKey="average_progress" 
              name="Average Progress (%)"
              radius={[4, 4, 0, 0]}
              fill="#3b82f6"
              opacity={0.7}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Status Summary</h4>
          <div className="space-y-2">
            {data.map((item) => (
              <div key={item.status} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded mr-2" 
                    style={{ backgroundColor: STATUS_COLORS[item.status as keyof typeof STATUS_COLORS] || '#6b7280' }}
                  ></div>
                  <span className="text-sm">{formatStatus(item.status)}</span>
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{item.count}</span> workshops
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Performance Insights</h4>
          <div className="space-y-2 text-sm">
            {[...data]
              .sort((a, b) => b.average_progress - a.average_progress)
              .slice(0, 3)
              .map((item, index) => (
                <div key={item.status} className="flex items-center justify-between p-2 bg-blue-50 rounded">
                  <span>{formatStatus(item.status)}</span>
                  <span className="font-medium text-blue-600">{item.average_progress.toFixed(1)}%</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceByStatusChart;
